import { useState, useEffect } from 'react';
import { Save, DollarSign, Settings as SettingsIcon, MapPin, Scale, Ruler, Bell, Calendar, RotateCcw, AlertCircle, Loader2, Monitor, LayoutTemplate, ShieldAlert, Copy, ExternalLink, Building2, ChevronDown, Sun, Moon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSystemConfig, SystemConfig } from '../lib/system-config';
import { FormField } from '../components/ui/FormField';
import { useAuthStore } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';

// Helper: Strict Clamping
const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

export const Settings = () => {
    // Authoritative store
    const { config, updateSection, resetDefaults } = useSystemConfig();
    const { user, token } = useAuthStore();
    const { theme, setTheme } = useTheme();

    const [loading, setLoading] = useState(false);
    const [locError, setLocError] = useState<string | null>(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Local raw input strings for Appointment Rules & Auto-Refresh to prevent silent keystroke coercion (e.g. -5 -> 605 -> 480)
    const [apptRawInputs, setApptRawInputs] = useState({
        defaultDurationMins: String(config.appointments.defaultDurationMins),
        bufferBeforeMins: String(config.appointments.bufferBeforeMins),
        bufferAfterMins: String(config.appointments.bufferAfterMins),
        lateAfterMins: String(config.appointments.lateAfterMins),
        geofenceRadiusKm: String(config.appointments.geofenceRadiusKm),
        autoRefreshSeconds: String(config.defaults.autoRefreshSeconds),
    });
    const [apptErrors, setApptErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        setApptRawInputs({
            defaultDurationMins: String(config.appointments.defaultDurationMins),
            bufferBeforeMins: String(config.appointments.bufferBeforeMins),
            bufferAfterMins: String(config.appointments.bufferAfterMins),
            lateAfterMins: String(config.appointments.lateAfterMins),
            geofenceRadiusKm: String(config.appointments.geofenceRadiusKm),
            autoRefreshSeconds: String(config.defaults.autoRefreshSeconds),
        });
    }, [
        config.appointments.defaultDurationMins,
        config.appointments.bufferBeforeMins,
        config.appointments.bufferAfterMins,
        config.appointments.lateAfterMins,
        config.appointments.geofenceRadiusKm,
        config.defaults.autoRefreshSeconds,
    ]);

    // Warehouse identity (fetched from API)
    const [whName, setWhName] = useState('');
    const [whCity, setWhCity] = useState('');
    const [whBookingId, setWhBookingId] = useState('');
    const [whTimezone, setWhTimezone] = useState('');

    useEffect(() => {
        if (!user?.warehouseId || !token) return;
        fetch(`/api/warehouses/${user.warehouseId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
        })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (d?.warehouse?.name) setWhName(d.warehouse.name);
                if (d?.warehouse?.city) setWhCity(d.warehouse.city);
                if (d?.warehouse?.publicBookingId) setWhBookingId(d.warehouse.publicBookingId);
                if (d?.warehouse?.timeZone) setWhTimezone(d.warehouse.timeZone);
            })
            .catch(() => {});
    }, [user?.warehouseId, token]);

    const handleSave = () => {
        // Validate raw numeric inputs explicitly — never silently coerce negative or out-of-range numbers
        const nextErrors: Record<string, string> = {};
        const parsedDuration = Number(apptRawInputs.defaultDurationMins);
        const parsedBufferBefore = Number(apptRawInputs.bufferBeforeMins);
        const parsedBufferAfter = Number(apptRawInputs.bufferAfterMins);
        const parsedLateAfter = Number(apptRawInputs.lateAfterMins);
        const parsedGeofence = Number(apptRawInputs.geofenceRadiusKm);
        const parsedRefresh = Number(apptRawInputs.autoRefreshSeconds);

        if (apptRawInputs.defaultDurationMins.trim() === '' || !Number.isFinite(parsedDuration) || parsedDuration < 5 || parsedDuration > 480) {
            nextErrors.defaultDurationMins = 'Default duration must be between 5 and 480 minutes.';
        }
        if (apptRawInputs.bufferBeforeMins.trim() === '' || !Number.isFinite(parsedBufferBefore) || parsedBufferBefore < 0 || parsedBufferBefore > 120) {
            nextErrors.bufferBeforeMins = 'Buffer before must be between 0 and 120 minutes (cannot be negative).';
        }
        if (apptRawInputs.bufferAfterMins.trim() === '' || !Number.isFinite(parsedBufferAfter) || parsedBufferAfter < 0 || parsedBufferAfter > 120) {
            nextErrors.bufferAfterMins = 'Buffer after must be between 0 and 120 minutes (cannot be negative).';
        }
        if (apptRawInputs.lateAfterMins.trim() === '' || !Number.isFinite(parsedLateAfter) || parsedLateAfter < 0 || parsedLateAfter > 240) {
            nextErrors.lateAfterMins = 'Late arrival threshold must be between 0 and 240 minutes (cannot be negative).';
        }
        if (config.appointments.geofenceRequired && (apptRawInputs.geofenceRadiusKm.trim() === '' || !Number.isFinite(parsedGeofence) || parsedGeofence < 0 || parsedGeofence > 50)) {
            nextErrors.geofenceRadiusKm = 'Verification radius must be between 0 and 50 km.';
        }
        if (apptRawInputs.autoRefreshSeconds.trim() === '' || !Number.isFinite(parsedRefresh) || parsedRefresh < 0 || parsedRefresh > 3600) {
            nextErrors.autoRefreshSeconds = 'Auto-refresh interval must be between 0 and 3600 seconds.';
        }

        if (Object.keys(nextErrors).length > 0) {
            setApptErrors(nextErrors);
            toast.error(Object.values(nextErrors)[0]);
            return;
        }
        setApptErrors({});
        setLoading(true);
        try {
            // 1. Strict Validation & Clamping (Sanitize data before "saving")
            const sanitizedFinancial = {
                ...config.financial,
                graceMinutes: clamp(config.financial.graceMinutes, 0, 180),
                detentionRate: clamp(config.financial.detentionRate, 0, 500)
            };

            const sanitizedAppointments = {
                ...config.appointments,
                defaultDurationMins: Math.round(parsedDuration),
                lateAfterMins: Math.round(parsedLateAfter),
                bufferBeforeMins: Math.round(parsedBufferBefore),
                bufferAfterMins: Math.round(parsedBufferAfter),
                geofenceRadiusKm: config.appointments.geofenceRequired ? parsedGeofence : clamp(config.appointments.geofenceRadiusKm, 0, 50)
            };

            const autoRefreshVal = Math.round(parsedRefresh);

            // Validate Start Page (must start with /)
            let startPageVal = config.defaults.startPage;
            if (!startPageVal || !startPageVal.startsWith('/')) {
                startPageVal = '/dashboard';
            }

            const sanitizedDefaults = {
                ...config.defaults,
                autoRefreshSeconds: autoRefreshVal as SystemConfig['defaults']['autoRefreshSeconds'], // Type cast safe after validation
                startPage: startPageVal as SystemConfig['defaults']['startPage']
            };

            // Apply sanitized values to store immediately (persist middleware handles storage)
            updateSection('financial', sanitizedFinancial);
            updateSection('appointments', sanitizedAppointments);
            updateSection('defaults', sanitizedDefaults);

            // 2. Persist (Implicit) - Simulate Latency to allow UI to reflect 'saving' state
            setTimeout(() => {
                setLoading(false);
                toast.success('System configuration saved successfully');
            }, 600);

        } catch (err: any) {
            setLoading(false);
            toast.error(err.message || 'Failed to save settings');
        }
    };

    const handleReset = () => {
        if (window.confirm('Are you sure you want to reset all system settings to defaults?')) {
            resetDefaults();
            toast.success('Restored default settings');
        }
    };

    const handleGetLocation = () => {
        setLocError(null);
        if (!('geolocation' in navigator)) {
            setLocError('Geolocation not supported');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                // Update system config directly
                updateSection('facility', {
                    latitude: lat.toFixed(6),
                    longitude: lng.toFixed(6)
                });
                toast.success('Facility location updated');
            },
            (err) => {
                console.error(err);
                setLocError('Location access denied. Please enable in browser settings.');
                toast.error('Location access denied');
            }
        );
    };

    return (
        <div className="p-6 max-w-5xl mx-auto min-h-screen pb-20">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pl-12 md:pl-0">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                        <SettingsIcon className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                    <h1 className="text-3xl font-bold text-foreground tracking-tight">Facility Settings</h1>
                        <p className="text-muted-foreground">Configure how your facility operates in BayWhiz</p>
                    </div>
                </div>
                <button
                    onClick={handleReset}
                    className="btn btn-outline text-muted-foreground hover:text-red-500 hover:border-red-500/50"
                >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset Defaults
                </button>
            </div>

            <div className="grid gap-8">

                {/* 0. Warehouse Identity */}
                {whBookingId && (
                    <div className="card p-6 border-l-4 border-l-primary">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                            <Building2 className="text-primary" /> Warehouse Identity
                        </h2>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Facility</label>
                                    <p className="text-lg font-bold text-foreground mt-0.5">
                                        {whName}{whCity ? ` – ${whCity}` : ''}
                                    </p>
                                </div>
                                {whTimezone && (
                                    <div>
                                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Timezone</label>
                                        <p className="text-sm text-foreground mt-0.5">{whTimezone.replace(/_/g, ' ')}</p>
                                    </div>
                                )}
                            </div>

                            <div className="bg-muted/30 p-4 rounded-lg border border-border">
                                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    Warehouse ID (share with carriers)
                                </label>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <span className="text-2xl font-mono font-bold text-foreground tracking-wide">{whBookingId}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(whBookingId);
                                            toast.success('Warehouse ID copied to clipboard');
                                        }}
                                        className="btn btn-secondary px-3 py-1.5 flex items-center gap-1.5 text-xs"
                                        title="Copy Warehouse ID"
                                    >
                                        <Copy className="w-3.5 h-3.5" />
                                        Copy ID
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const link = `${window.location.origin}/carrier-request?warehouse=${encodeURIComponent(whBookingId)}`;
                                            navigator.clipboard.writeText(link);
                                            toast.success('Carrier booking link copied to clipboard');
                                        }}
                                        className="btn btn-outline px-3 py-1.5 flex items-center gap-1.5 text-xs"
                                        title="Copy Shareable Carrier Booking Link"
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        Copy Booking Link
                                    </button>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Carriers use this ID or booking link to request appointments at your facility.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                {/* 1. Facility Location */}
                <div className="card p-6 border-l-4 border-l-blue-500">
                    <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                        <MapPin className="text-blue-500" /> Facility Location
                    </h2>
                    <p className="text-sm text-muted-foreground mb-6">Coordinates power weather alerts and proximity features. Use the button below or enter manually.</p>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-muted/30 p-4 rounded-lg border border-border flex flex-col justify-between">
                            <div className="mb-4">
                                <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Current Location</label>
                                <div className="mt-2 text-2xl font-mono text-foreground font-bold">
                                    {(config.facility.latitude && config.facility.longitude)
                                        ? `${parseFloat(config.facility.latitude).toFixed(4)}, ${parseFloat(config.facility.longitude).toFixed(4)}`
                                        : <span className="text-amber-500 flex items-center gap-2"><AlertCircle size={18} /> Not Set</span>}
                                </div>
                            </div>
                            <div>
                                <button
                                    onClick={handleGetLocation}
                                    className="btn btn-secondary w-full"
                                >
                                    <MapPin className="w-4 h-4 mr-2" />
                                    Get Current Location
                                </button>
                                {locError && <p className="text-red-500 text-xs mt-2 text-center">{locError}</p>}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <FormField
                                label="Latitude"
                                value={config.facility.latitude}
                                onChange={(val) => updateSection('facility', { latitude: val })}
                                placeholder="e.g. 49.0521"
                            />
                            <FormField
                                label="Longitude"
                                value={config.facility.longitude}
                                onChange={(val) => updateSection('facility', { longitude: val })}
                                placeholder="e.g. -122.3045"
                            />
                        </div>
                    </div>
                </div>

                {/* 2. Financial & Business Rules */}
                <div className="card p-6 border-l-4 border-l-emerald-500">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <DollarSign className="text-emerald-500" /> Financial & Units
                    </h2>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Currency */}
                        <div className="form-group">
                            <label className="label">Currency</label>
                            <div className="flex bg-muted rounded-lg p-1 border border-border">
                                {(['USD', 'CAD'] as const).map(curr => (
                                    <button
                                        key={curr}
                                        onClick={() => updateSection('financial', { currency: curr })}
                                        className={`flex-1 py-1.5 rounded-md text-sm font-bold transition-all ${config.financial.currency === curr
                                            ? 'bg-white dark:bg-background shadow-sm text-foreground border border-slate-200 dark:border-transparent'
                                            : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                    >
                                        {curr}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="md:col-span-2 p-4 bg-slate-50 dark:bg-muted/20 rounded-lg border border-border">
                            <p className="text-sm text-muted-foreground">
                                <strong>Detention Free Time</strong> and <strong>Detention Rate ($/hr)</strong> are configured per-warehouse in{' '}
                                <a href="/facilities" className="text-cyan-500 hover:underline font-semibold">Facilities → Operating Hours</a>.
                            </p>
                        </div>
                    </div>

                    <div className="w-full h-px bg-border my-6" />

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Weight Unit */}
                        <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border">
                            <div>
                                <h3 className="font-semibold flex items-center gap-2">
                                    <Scale size={16} /> Weight Standard
                                </h3>
                            </div>
                            <div className="flex bg-muted rounded-lg p-1 border border-border">
                                {(['LBS', 'KG'] as const).map(u => (
                                    <button
                                        key={u}
                                        onClick={() => updateSection('units', { weight: u })}
                                        className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${config.units.weight === u
                                            ? 'bg-white dark:bg-background shadow-sm text-foreground border border-slate-200 dark:border-transparent'
                                            : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                    >
                                        {u}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Distance Unit */}
                        <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border">
                            <div>
                                <h3 className="font-semibold flex items-center gap-2">
                                    <Ruler size={16} /> Distance Standard
                                </h3>
                            </div>
                            <div className="flex bg-muted rounded-lg p-1 border border-border">
                                {(['MI', 'KM'] as const).map(u => (
                                    <button
                                        key={u}
                                        onClick={() => updateSection('units', { distance: u })}
                                        className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${config.units.distance === u
                                            ? 'bg-white dark:bg-background shadow-sm text-foreground border border-slate-200 dark:border-transparent'
                                            : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                    >
                                        {u}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Appointment Rules */}
                <div className="card p-6 border-l-4 border-l-indigo-500">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <Calendar className="text-indigo-500" /> Appointment Rules
                    </h2>

                    <div className="grid md:grid-cols-3 gap-6">
                        <FormField
                            label="Default Duration (Mins)"
                            type="number"
                            value={apptRawInputs.defaultDurationMins}
                            onChange={(val) => {
                                setApptRawInputs(prev => ({ ...prev, defaultDurationMins: val }));
                                setApptErrors(prev => { const { defaultDurationMins, ...r } = prev; return r; });
                            }}
                            helpText="How long each appointment slot is by default (5–480 mins)."
                            error={apptErrors.defaultDurationMins}
                        />
                        <FormField
                            label="Buffer Before (Mins)"
                            type="number"
                            value={apptRawInputs.bufferBeforeMins}
                            onChange={(val) => {
                                setApptRawInputs(prev => ({ ...prev, bufferBeforeMins: val }));
                                setApptErrors(prev => { const { bufferBeforeMins, ...r } = prev; return r; });
                            }}
                            helpText="Padding added before each appointment (0–120 mins)."
                            error={apptErrors.bufferBeforeMins}
                        />
                        <FormField
                            label="Buffer After (Mins)"
                            type="number"
                            value={apptRawInputs.bufferAfterMins}
                            onChange={(val) => {
                                setApptRawInputs(prev => ({ ...prev, bufferAfterMins: val }));
                                setApptErrors(prev => { const { bufferAfterMins, ...r } = prev; return r; });
                            }}
                            helpText="Padding added after each appointment (0–120 mins)."
                            error={apptErrors.bufferAfterMins}
                        />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <FormField
                            label="Late Arrival Threshold (Mins)"
                            type="number"
                            value={apptRawInputs.lateAfterMins}
                            onChange={(val) => {
                                setApptRawInputs(prev => ({ ...prev, lateAfterMins: val }));
                                setApptErrors(prev => { const { lateAfterMins, ...r } = prev; return r; });
                            }}
                            helpText="A truck is marked late if it checks in after this many minutes past its appointment time (0–240 mins)."
                            error={apptErrors.lateAfterMins}
                        />
                    </div>
                </div>

                {/* 4. Appearance & UX */}
                <div className="card p-6 border-l-4 border-l-violet-500">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <Monitor className="text-violet-500" /> Appearance & UX
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Display */}
                        <div className="form-group">
                            <label className="label">Display</label>
                            <div
                                role="radiogroup"
                                aria-label="Display brightness mode"
                                className="flex bg-muted rounded-lg p-1 border border-border"
                            >
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={theme === 'light'}
                                    aria-label="Day shift mode - high ambient light"
                                    onClick={() => {
                                        setTheme('light');
                                        updateSection('appearance', { theme: 'light' });
                                    }}
                                    className={`flex-1 py-1.5 px-3 rounded-md text-sm font-bold flex items-center justify-center gap-1.5 transition-all duration-200 ${theme === 'light'
                                        ? 'bg-white dark:bg-background shadow-sm text-foreground border border-slate-200 dark:border-transparent'
                                        : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                >
                                    <Sun size={15} aria-hidden="true" />
                                    <span>Day</span>
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={theme === 'dark'}
                                    aria-label="Night shift mode - low ambient light"
                                    onClick={() => {
                                        setTheme('dark');
                                        updateSection('appearance', { theme: 'dark' });
                                    }}
                                    className={`flex-1 py-1.5 px-3 rounded-md text-sm font-bold flex items-center justify-center gap-1.5 transition-all duration-200 ${theme === 'dark'
                                        ? 'bg-white dark:bg-background shadow-sm text-foreground border border-slate-200 dark:border-transparent'
                                        : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                >
                                    <Moon size={15} aria-hidden="true" />
                                    <span>Night</span>
                                </button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Choose how BayWhiz appears on this device.</p>
                        </div>

                        {/* Density */}
                        <div className="form-group">
                            <label className="label">Density</label>
                            <div className="flex bg-muted rounded-lg p-1 border border-border">
                                {(['comfortable', 'compact'] as const).map(d => (
                                    <button
                                        key={d}
                                        onClick={() => updateSection('appearance', { density: d })}
                                        className={`flex-1 py-1.5 rounded-md text-sm font-bold capitalize transition-all ${config.appearance.density === d
                                            ? 'bg-white dark:bg-background shadow-sm text-foreground border border-slate-200 dark:border-transparent'
                                            : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                    >
                                        {d}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Compact reduces padding/button heights</p>
                        </div>
                    </div>
                </div>

                {/* 5. User Defaults */}
                <div className="card p-6 border-l-4 border-l-cyan-500">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <LayoutTemplate className="text-cyan-500" /> Defaults
                    </h2>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Start Page */}
                        <div className="form-group">
                            <label className="label">Start Page</label>
                            <select
                                className="field"
                                value={config.defaults.startPage}
                                onChange={(e) => updateSection('defaults', { startPage: e.target.value as SystemConfig['defaults']['startPage'] })}
                            >
                                <option value="/dashboard">Dashboard</option>
                                <option value="/docks">Live Docks</option>
                                <option value="/appointments">Appointments</option>
                                <option value="/orders">Orders</option>
                                <option value="/carriers">Carriers</option>
                                <option value="/analytics">Analytics</option>
                            </select>
                            <p className="text-xs text-muted-foreground mt-1">Page to load after login</p>
                        </div>

                        {/* Auto-Refresh */}
                        <div className="form-group">
                            <label className="label">Auto-Refresh Interval (Seconds)</label>
                            <input
                                type="number"
                                className={`field ${apptErrors.autoRefreshSeconds ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                min={0}
                                max={3600}
                                step={5}
                                value={apptRawInputs.autoRefreshSeconds}
                                onChange={(e) => {
                                    setApptRawInputs(prev => ({ ...prev, autoRefreshSeconds: e.target.value }));
                                    setApptErrors(prev => { const { autoRefreshSeconds, ...r } = prev; return r; });
                                }}
                            />
                            {apptErrors.autoRefreshSeconds ? (
                                <p className="text-xs text-red-500 mt-1">{apptErrors.autoRefreshSeconds}</p>
                            ) : (
                                <p className="text-xs text-muted-foreground mt-1">0 disables auto-refresh (0–3600s)</p>
                            )}
                        </div>

                        {/* Dock Sort */}
                        <div className="form-group">
                            <label className="label">Default Dock Sort</label>
                            <select
                                className="field"
                                value={config.defaults.dockSort}
                                onChange={(e) => updateSection('defaults', { dockSort: e.target.value as SystemConfig['defaults']['dockSort'] })}
                            >
                                <option value="number">Dock Number</option>
                                <option value="status">Status Priority</option>
                                <option value="activity">Recent Activity</option>
                            </select>
                        </div>

                        {/* Appt View */}
                        <div className="form-group">
                            <label className="label">Appointment View</label>
                            <div className="flex bg-muted rounded-lg p-1 border border-border w-max">
                                {(['day', 'week'] as const).map(v => (
                                    <button
                                        key={v}
                                        onClick={() => updateSection('defaults', { appointmentsDefaultView: v })}
                                        className={`px-4 py-1.5 rounded-md text-sm font-bold capitalize transition-all ${config.defaults.appointmentsDefaultView === v
                                            ? 'bg-white dark:bg-background shadow-sm text-foreground border border-slate-200 dark:border-transparent'
                                            : 'text-muted-foreground hover:text-foreground'
                                            }`}
                                    >
                                        {v}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 6. Safety */}
                <div className="card p-6 border-l-4 border-l-red-500">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <ShieldAlert className="text-red-500" /> Safety
                    </h2>
                    <div className="flex items-center p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors shadow-sm">
                        <input
                            type="checkbox"
                            checked={config.safety.confirmDestructiveActions}
                            onChange={(e) => updateSection('safety', { confirmDestructiveActions: e.target.checked })}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary mr-3"
                        />
                        <span className="text-sm font-medium">Require confirmation for destructive actions (Delete, Cancel POs, etc.)</span>
                    </div>
                </div>

                {/* 7. Notifications */}
                <div className="card p-6 border-l-4 border-l-amber-500">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <Bell className="text-amber-500" /> Notifications
                    </h2>

                    <div className="grid md:grid-cols-2 gap-4">
                        {[
                            { key: 'emailLate', label: 'Email alerts when appointment is marked LATE' },
                            { key: 'emailDwellBreach', label: 'Email alerts for dwell time breaches (>2h)' },
                            { key: 'emailExceptions', label: 'Email alerts for dock exceptions' },
                            { key: 'dailySummary', label: 'Send daily operational summary report' },
                        ].map((item) => (
                            <div key={item.key} className="flex items-center p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors shadow-sm">
                                <input
                                    type="checkbox"
                                    checked={config.notifications[item.key as keyof typeof config.notifications]}
                                    onChange={(e) => updateSection('notifications', { [item.key]: e.target.checked })}
                                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary mr-3"
                                />
                                <span className="text-sm font-medium">{item.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {/* 8. Advanced Settings (Collapsible) */}
                <div className="card p-6 border-l-4 border-l-slate-400">
                    <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="w-full flex items-center justify-between text-left"
                    >
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <SettingsIcon className="text-slate-400" /> Advanced
                        </h2>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                    </button>
                    <p className="text-sm text-muted-foreground mt-1">Optional settings for location verification and advanced workflows.</p>

                    {showAdvanced && (
                        <div className="mt-6 space-y-4">
                            <div className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg border border-border">
                                <input
                                    type="checkbox"
                                    checked={config.appointments.geofenceRequired}
                                    onChange={(e) => updateSection('appointments', { geofenceRequired: e.target.checked })}
                                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <div>
                                    <span className="text-sm font-medium">Require location verification for driver check-in</span>
                                    <p className="text-xs text-muted-foreground">When enabled, drivers must be within a set radius of the facility to check in via mobile.</p>
                                </div>
                            </div>

                            {config.appointments.geofenceRequired && (
                                <div className="ml-6">
                                    <FormField
                                        label="Verification Radius (km)"
                                        type="number"
                                        value={apptRawInputs.geofenceRadiusKm}
                                        onChange={(val) => {
                                            setApptRawInputs(prev => ({ ...prev, geofenceRadiusKm: val }));
                                            setApptErrors(prev => { const { geofenceRadiusKm, ...r } = prev; return r; });
                                        }}
                                        helpText="How close a driver must be to your facility to check in (0–50 km)."
                                        error={apptErrors.geofenceRadiusKm}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Save Footer */}
                <div className="flex justify-end pt-6 sticky bottom-6 z-40">
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="btn btn-primary btn-lg shadow-xl shadow-primary/20 flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Save Configuration
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Settings;
